import { useId } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { FlexGap } from '@treams/treams-ui/common/FlexGap';
import { Skeleton } from '@treams/treams-ui/common/Skeleton';
import { Heading, Paragraph } from '@treams/treams-ui/common/Text';
import { tokens } from '@treams/treams-ui/theme/tokens';

import { type RewardSummary as RewardSummaryData } from './buildRewardSummary';

const SummaryCard = styled.div<{ $attention?: boolean }>`
	flex: 1 1 180px;
	min-width: 0;
	box-sizing: border-box;
	display: flex;
	flex-direction: column;
	gap: ${tokens.gap.element}px;
	padding: ${tokens.padding.container}px;
	border-radius: ${tokens.radius.md}px;
	background: ${(props) => (props.$attention ? tokens.warning.background : tokens.surface.white)};
	border: 1px solid ${(props) => (props.$attention ? tokens.warning.border : tokens.border.subtle)};
	box-shadow: ${tokens.shadow.xs};
`;

const Value = styled.div`
	color: ${tokens.text.primary};
	font-family: ${tokens.typography.fontFamily};
	font-size: ${tokens.typography.sizes.h1}px;
	font-weight: ${tokens.typography.weights.bold};
	line-height: ${tokens.typography.lineHeights.heading};
	letter-spacing: -0.005em;
`;

function formatGenderPayGap(gap: number, language: string): string {
	return new Intl.NumberFormat(language, {
		style: 'percent',
		minimumFractionDigits: 1,
		maximumFractionDigits: 1,
	}).format(gap);
}

function Metric(props: { label: string; value: string; attention?: boolean }) {
	const id = useId();

	return (
		<SummaryCard role="group" aria-labelledby={id} $attention={props.attention}>
			<Paragraph id={id} small style={props.attention ? { color: tokens.warning.text } : undefined}>
				{props.label}
			</Paragraph>
			<Value>{props.value}</Value>
		</SummaryCard>
	);
}

export function RewardSummary(props: { summary: RewardSummaryData }) {
	const { i18n, t } = useTranslation();
	const { summary } = props;

	return (
		<FlexGap gap={tokens.gap.group} flexWrap="wrap">
			<Metric label={t('analytics.reward.summary.withScale')} value={String(summary.withScale)} />
			<Metric
				label={t('analytics.reward.summary.withinScale')}
				value={String(summary.withinScale)}
			/>
			<Metric
				label={t('analytics.reward.summary.outsideScale')}
				value={String(summary.outsideScale)}
				attention
			/>
			<Metric
				label={t('analytics.reward.summary.genderPayGap')}
				value={
					summary.genderPayGap == null
						? '–'
						: formatGenderPayGap(summary.genderPayGap, i18n.language)
				}
			/>
		</FlexGap>
	);
}

RewardSummary.Skeleton = function RewardSummarySkeleton() {
	return (
		<FlexGap gap={tokens.gap.group} flexWrap="wrap">
			<Skeleton.Repeat count={4}>
				<SummaryCard>
					<Paragraph.Skeleton width={120} lines={1} />
					<Heading.Skeleton width={48} />
				</SummaryCard>
			</Skeleton.Repeat>
		</FlexGap>
	);
};
